import React, { Component } from 'react';
import { compose, Query } from '../../reactApolloCompat';
import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';
import Downshift from 'downshift';
import cx from 'classnames';
import MenuItem from '@mui/material/MenuItem';
import { Popper, Typography } from '@mui/material';
import TextField from './TextField';
import Spinner from '../loading/Spinner';
import { getClient } from '../../utils/apollo';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '../icon/SearchIcon';
import { getValidationDecorations } from '../../utils/validation';
import { CloseIcon } from '../IconIndex';
import { joinDefined } from '../../utils/strings';
import { getDownshiftInputProps, isDefinedAndDifferent } from '../../utils/objects';

/**
 * Autocomplete that supports searching for and selecting results from
 * the gql database.
 *
 * For custom handling behaviour you can provide the `value` and `onSelect`
 * callback handler passed through props.
 *
 * To change the field that will be displayed, you can specify labelField or labelFieldFunc().
 * To change the field that will be used as the value & lookup query, specify valueField.
 * Alternatively it can accept a string as the value/label.
 */
class GqlAutocomplete extends Component {
    static defaultProps = {
        form: undefined, //use DataForm
        name: undefined, //use name for field in DataForm
        value: undefined, //if no DataForm use object like { ID: 1, Name: "Hospital" } or a string
        onSelect: undefined, //additional custom behaviours
        placeholder: 'Search...',
        categories: undefined, //query filter, typically Object like [ { key:'', label:'' } ],
        readAllQuery: undefined, //query to read ALL items from database
        readOneQuery: undefined, //query to read single item by ID from the database
        minChars: 3, //wait for this many characters before executing query
        valueField: 'ID', //if the value is an object, this is what to match/lookup
        labelField: 'Name', //if the value is an object, this is what to display for user
        labelFieldFunc: undefined //if this is a function, use this to display value to user
    };
    state = {
        value: undefined,
        searchTerm: '',
        focused: false,
        busy: false,
        valueIsListed: false
    };
    _isMounted = false;

    componentDidMount() {
        this._isMounted = true;
        const nextValue = this.props.value || this.props.form?.getField(this.props.name);
        if (undefined !== nextValue) {
            // empty on first load but value on remount
            this.resetInitialState(nextValue, true);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { valueField, name, form } = this.props;
        const prevForm = prevProps.form;
        const prevPropsValue = prevProps.value ?? prevForm?.getField(prevProps.name);
        const nextPropsValue = this.props.value ?? form?.getField(name);
        const currStateValue = this.state.value;
        let prevValue = prevPropsValue;
        let nextValue = nextPropsValue;
        let stateValue = currStateValue;
        if (typeof nextPropsValue !== 'string') {
            prevValue = prevPropsValue?.[valueField];
            nextValue = nextPropsValue?.[valueField];
            stateValue = stateValue?.[valueField];
        }
        if (prevValue !== nextValue) {
            // new value
            this.resetInitialState(nextPropsValue, false);
        } else if (stateValue === undefined) {
            // value is pending
            this.resetInitialState(nextPropsValue, true);
        }
        const { searchTerm: prevSearchTerm } = prevState;
        const { searchTerm: lastSearchTerm } = this.state;
        if (prevSearchTerm !== lastSearchTerm) this.updateForm();
    }

    resetInitialState(nextValue, isMount) {
        const { name, findById, readOneQuery, categories, queryClient, valueField } = this.props;
        const { value: stateValue, busy } = this.state;

        //on first call, we don't need to compare props. just set the initial state
        //on subsequent calls, we're comparing old props to new props
        if (!isMount) {
            //prime the first value
            const { form: lastForm } = this.props;
            const lastValue = (lastForm && lastForm.getField(name)) || this.props.value || null;
            if (nextValue === lastValue && nextValue === stateValue) return;
        }

        if (nextValue !== stateValue) {
            let newValue = nextValue;
            if (typeof nextValue !== 'string') {
                newValue = nextValue?.[valueField];
            }
            if (!isDefinedAndDifferent(newValue, stateValue?.[valueField])) return;
            if (!busy && findById && readOneQuery) {
                this.setState({ busy: true });
                getValueById(queryClient, nextValue, readOneQuery, categories)
                    .then(result => {
                        if (!this._isMounted) return;
                        this.setState({ busy: false });
                        if (!result) return;
                        const clonedResult = { ...result };
                        this.setState({ value: clonedResult });
                        this.setSearchTerm(clonedResult);
                    })
                    .catch(() => {
                        if (!this._isMounted) return;
                        this.setState({ busy: false });
                    });
            } else {
                this.setState({ value: nextValue });
                this.setSearchTerm(nextValue);
            }
        }
    }

    setSearchTerm = newValue => {
        const { labelField: newLabelField, labelFieldFunc: newLabelFieldFunc } = this.props;
        const { searchTerm } = this.state;

        const lastSearchTerm = searchTerm;

        let newSearchTerm;
        if (typeof newValue === 'object') {
            if (newLabelFieldFunc) {
                newSearchTerm = newLabelFieldFunc(newValue);
            } else {
                newSearchTerm = newValue.hasOwnProperty(newLabelField) ? newValue[newLabelField] : null;
            }
        } else {
            newSearchTerm = newValue;
        }

        if (lastSearchTerm === newSearchTerm) return;

        this.setState({ searchTerm: newSearchTerm });
    };

    onInputRef = input => {
        this.input = input;
    };

    onInputFocus = () => {
        this.setState({ focused: true });
    };

    onInputBlur = () => {
        const { allowUnlisted } = this.props;
        this.setState({ focused: false }, () => {
            const { value } = this.state;
            !allowUnlisted && this.setSearchTerm(value || '');
        });
    };

    onClear = e => {
        const { onSelect, form, name } = this.props;
        if (form && name) {
            if (form.setField && !form.setState) {
                form.setState = form.setField;
            }
            const { valueField, labelField } = this.props;
            form.setState({ [name]: 'ID' === valueField ? { [valueField]: null, [labelField]: null } : null });
        }
        this.setState({ value: null, searchTerm: '' }, () => onSelect && onSelect(e, null));
    };

    updateForm() {
        const { name, form, allowUnlisted } = this.props;
        const { value, valueIsListed } = this.state;

        if (!form || !name || (!allowUnlisted && !valueIsListed)) return;

        if (form.setField && !form.setState) form.setState = form.setField;

        form.setState({ [name]: value });
    }

    onItemSelected = (e, result) => {
        const { onSelect, findById, clearOnSelect, form, name } = this.props;

        if (onSelect) onSelect(e, result);

        if (e.defaultPrevented) return;

        if (clearOnSelect) {
            this.setState({ value: null, searchTerm: '', valueIsListed: true });
        } else {
            const { valueField } = this.props;
            const valueToUse = findById && result ? result[valueField] : result;
            this.setState({ value: valueToUse, valueIsListed: true });
            this.setSearchTerm(valueToUse);
        }

        if (this.input) this.input.blur();

        if (form && !form.getValidation) {
            console.warn(name + ' form does not contain validation function!');
            return;
        }

        if (form && name && form.getValidation(name, true).shouldUpdate) this.forceUpdate();
    };

    getQueryVariables() {
        const { categories, canSearchByKey, extraVariables, limit = 10 } = this.props;
        const { searchTerm } = this.state;

        let variables = {
            limit,
            filter: categories,
            ...extraVariables
        };

        const term = !!searchTerm ? ('' + searchTerm).trim() : '';
        const terms = term.split(' ');

        if (canSearchByKey && !isNaN(parseInt(terms[0], 10))) {
            // If the term is non-numeric then we do a `contains` search
            // otherwise we search for a `key` match
            variables.key = terms[0];
            variables.contains = null;
        } else {
            variables.key = null;
            variables.contains = term;
        }

        return variables;
    }

    render() {
        const {
            readAllQuery,
            convertResults,
            convertTotalCount,
            client: queryClient,
            name,
            minChars,
            classes,
            className
        } = this.props;
        const { focused } = this.state;
        const variables = this.getQueryVariables();

        return (
            <Query
                query={readAllQuery}
                skip={
                    !focused ||
                    (!variables.contains && !variables.key) ||
                    (!!variables.contains && variables.contains.length < minChars)
                }
                variables={variables}
                client={queryClient}
                context={{ debounceKey: 2, debounceTimeout: 500 }}
            >
                {({ data, loading, error, networkStatus }) => {
                    let results = null;
                    let totalCount = 0;
                    if (data && !loading) {
                        const propertyName = Object.keys(data)[0];
                        const objList = data[propertyName];
                        results = convertResults
                            ? convertResults(data)
                            : (objList && (objList.edges || objList).map(e => e.node || e)) || null;
                        totalCount = convertTotalCount
                            ? convertTotalCount(data)
                            : (objList && objList.pageInfo && objList.pageInfo.totalCount) || 0;
                    }
                    const isReallyLoading = results != null && loading && focused; //it seems to loading=true before a query is run
                    return (
                        <Downshift
                            id={name}
                            itemToString={item => {
                                if (!item) return '';
                                const { labelField, labelFieldFunc } = this.props;
                                return labelFieldFunc ? labelFieldFunc(item) : item[labelField] || '';
                            }}
                            onSelect={result =>
                                !!result && this.onItemSelected(this.createEvent('itemSelected'), { ...result })
                            }
                        >
                            {d => (
                                <div className={cx(classes.root, className)}>
                                    {this.renderInput(d, isReallyLoading, networkStatus)}
                                    <Popper
                                        open
                                        anchorEl={this.input?.parentElement}
                                        className={classes.popper}
                                        placement="bottom-start"
                                        style={{ width: this.input?.parentElement?.clientWidth }}
                                    >
                                        {this.renderResults(
                                            results,
                                            isReallyLoading,
                                            d,
                                            error,
                                            networkStatus,
                                            totalCount
                                        )}
                                    </Popper>
                                </div>
                            )}
                        </Downshift>
                    );
                }}
            </Query>
        );
    }

    renderInput(downshiftData, loading, networkStatus) {
        const {
            placeholder,
            id,
            name,
            label,
            multiple,
            required,
            allowUnlisted,
            addNewButton,
            variant,
            disabled,
            InputProps,
            icon = SearchIcon
        } = this.props;
        const { searchTerm } = this.state;

        const onChange = allowUnlisted
            ? e => this.setState({ searchTerm: e.target.value, valueIsListed: false, value: e.target.value })
            : e => this.setState({ searchTerm: e.target.value, valueIsListed: false });

        const decorations = getValidationDecorations(this.props);
        const AdornmentIcon = icon;
        const busy = networkStatus && networkStatus !== 7;
        const { searchField } = InputProps || {};
        const downshiftInputProps = getDownshiftInputProps(downshiftData.getInputProps);
        return (
            <div className={variant}>
                <TextField
                    id={id || name}
                    fullWidth
                    label={label}
                    multiple={multiple}
                    required={required}
                    validationResult={decorations.validationResult}
                    style={searchField && searchField.style}
                    className={searchField && searchField.className}
                    disabled={disabled}
                    InputProps={{
                        disableUnderline: true,
                        ...downshiftData.getInputProps({ placeholder }),
                        startAdornment: (
                            <InputAdornment position="start">
                                {!!busy && !!searchTerm ? (
                                    <span style={{ marginRight: 8 }}>
                                        <Spinner size="xs" />
                                    </span>
                                ) : (
                                    <AdornmentIcon className="input-search" />
                                )}
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {!!searchTerm ? (
                                    <span title="Remove">
                                    <CloseIcon
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: 'grey',
                                            padding: 1,
                                            fontSize: 12,
                                            borderRadius: 36,
                                            color: 'white'
                                        }}
                                        onClick={this.onClear}
                                    />
                                </span>
                                ) : (
                                    addNewButton || <></>
                                )}
                            </InputAdornment>
                        ),
                        inputProps: {
                            ...downshiftInputProps({
                                onKeyDown: event => {
                                    // Check for Home or End keys and allow if still typing
                                    if (event.key === 'Home' || event.key === 'End') {
                                        if ([null, -1].includes(downshiftData.highlightedIndex) || event.shiftKey) {
                                            event.preventDownshiftDefault = true;
                                        }
                                    }
                                }
                            }),
                            ref: this.onInputRef,
                            onChange,
                            value: searchTerm || '',
                            onFocus: this.onInputFocus,
                            onBlur: this.onInputBlur
                        }
                    }}
                />
            </div>
        );
    }

    renderResults(results, loading, downshiftData, error, networkStatus, totalCount = 0) {
        const { focused, searchTerm } = this.state;
        const { minChars, allowUnlisted } = this.props;
        const noResults = error || !results || !results.length;
        if (!focused || !searchTerm || (isNaN(searchTerm) && searchTerm.length < minChars)) return false;
        const { classes } = this.props;
        const busy = networkStatus && networkStatus !== 7;
        const s = !!results && totalCount - results.length === 1;
        return (
            <Paper className={cx(classes.results)} onMouseDown={e => e.preventDefault()}>
                {noResults ? (
                    busy ? (
                        <Typography className={classes.noResult}>Searching...</Typography>
                    ) : (
                        <Typography className={classes.noResult}>
                            {(error ? <span style={{ color: 'red' }}>{error.message}</span> : null) ||
                                'No results, ' + (!allowUnlisted ? 'try a different search?' : 'add this entry?')}
                        </Typography>
                    )
                ) : (
                    <div>{results.map((r, i) => this.renderResult(r, i, downshiftData))}</div>
                )}
                {results && totalCount > results.length && (
                    <Typography className={classes.noResult}>
                        There {!s ? 'are' : 'is'} {totalCount - results.length} more result{!s ? 's' : ''}, refine your
                        search?
                    </Typography>
                )}
            </Paper>
        );
    }

    renderResult = (result, index, downshiftData) => {
        const { classes, extraLabelField, extraLabelFunc } = this.props;
        const { valueField, labelField, labelFieldFunc } = this.props;

        const itemProps = downshiftData.getItemProps({ item: result, index });

        return (
            <MenuItem
                {...itemProps}
                className={classes.item}
                selected={downshiftData.highlightedIndex === index}
                key={result[valueField]}
            >
                <span className="item-label">
                    {extraLabelFunc && extraLabelFunc(result)}
                    {labelFieldFunc ? labelFieldFunc(result) : result[labelField]}
                    {extraLabelField && (
                        <small className={classes.itemExtra}>
                            {Array.isArray(extraLabelField)
                                ? joinDefined(
                                    extraLabelField.map(val => result[val]),
                                    ', '
                                )
                                : result[extraLabelField]}
                        </small>
                    )}
                </span>
            </MenuItem>
        );
    };

    createEvent = name => {
        var e = document.createEvent('Event');
        e.initEvent(name, true, true);
        return e;
    };
}

export const getValueById = async (queryClient, objOrID = {}, readOneQuery, categories = []) => {
    const client = queryClient || getClient();
    const ID = objOrID?.ID ?? objOrID;
    if (!ID) return null;
    const asyncQuery = await client.query({
        query: readOneQuery,
        variables: { id: ID, filter: categories }
    });

    const data = asyncQuery.data;
    if (!data) return null;
    const propertyName = Object.keys(data)[0];
    if (!propertyName) return null;

    return data[propertyName];
};

const styles = ({ palette }) => ({
    root: {
        position: 'relative',
        flex: '1 1 auto'
    },
    popper: {
        zIndex: 1300,
        clipPath: 'inset(0px -10px -10px -10px)', // no shadow at top
        minWidth: 360,
        '&[data-popper-placement=top-start]': {
            clipPath: 'inset(-10px -10px 0px -10px)' // no shadow at bottom
        }
    },
    results: {
        padding: `8px 0`,
        '&>div': {
            maxHeight: 'min(400px, 90vh)',
            overflowY: 'auto'
        }
    },
    item: {
        padding: '6px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        height: 'auto',
        minHeight: 24,
        '& .item-label': {
            '-webkit-box-orient': 'vertical',
            '-webkit-line-clamp': 2,
            display: '-webkit-box',
            width: '100%',
            whiteSpace: 'normal',
            overflow: 'hidden',
            fontSize: '0.875rem'
        }
    },
    itemExtra: {
        paddingLeft: 12,
        textOverflow: 'ellipsis'
    },
    spinner: {
        margin: '8px 16px'
    },
    noResult: {
        color: '#757575',
        padding: 16,
        '&:not(:first-child)': {
            borderTop: '1px solid #d5d5d5',
            padding: '8px 16px 0',
            textAlign: 'right'
        }
    },
    iconContainer: {
        alignSelf: 'center'
    },
    icon: {
        fontSize: 16,
        color: palette.action.active
    }
});

export default compose(withSnackbarMessage, withStyles(styles))(GqlAutocomplete);
